import {
  ApiError,
  type CatalogueItemResource,
  CatalogueItemsController_read_v1,
  type OutletResource,
  OutletsController_read_v1,
  type TicketResource,
  TicketsController_read_v1,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { IconArrowLeft, IconTicketOff } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { isNotFound } from '@/api/failure';
import { formatPaise } from '@/lib/money';

import { LoadFailed, Screen, ScreenFact } from '../back-office/Screen';
import { paths } from '../paths';
import { labelForItemKind } from './ticket-kinds';
import { describeRecordingGap, formatInstant } from './ticket-time';
import {
  isVoidable,
  isVoided,
  NO_VOID_REASON_EXPLAINED,
  NO_VOID_REASON_GIVEN,
  readVoidReason,
} from './ticket-void';
import { TicketLookup } from './TicketLookup';
import { VoidTicketDialog } from './VoidTicketDialog';

/**
 * One ticket's record: what a visitor disputing a charge is shown.
 *
 * ## Why a page and not a row
 *
 * The list answers "what did we sell today". This answers "what is *this*", and
 * the two questions want different things on screen. A row is scanned in a
 * column of its neighbours and carries what discriminates between them; a record
 * is read on its own with somebody standing at the counter, and what it needs is
 * the whole answer — what was sold, where, when the till printed it, when the
 * server took it, and whether it still stands.
 *
 * It also gives the lookup somewhere to land that can be refreshed, kept open in
 * a second tab, and read out to a colleague. That is the whole reason the
 * address is the ticket's id: a URL that survives a reload is what makes this
 * usable during a conversation rather than only during a click.
 *
 * ## Both clocks are stated here, and the list is not wrong to show one
 *
 * `ticket-time.ts` sets out the two — the till's `occurredAt` and the server's
 * `recordedAt` — and the list deliberately shows the first with the *distance*
 * underneath, because a second date column repeated down a table is a column
 * people stop reading. On a record, both are facts somebody may need: "when did
 * I pay" is answered by the till's clock and "when did the system have it" by
 * the server's, and a dispute about a ticket that synced hours later is exactly
 * where the second question gets asked. The sentence describing the gap is the
 * same one the list uses, from the same function, so the two screens never
 * describe one ticket differently.
 *
 * ## Names are a courtesy; the record is the record
 *
 * The outlet and the catalogue item are read separately, by id, and **a failure
 * to read either costs a name and never the screen**. The ticket itself already
 * carries every fact that decides a dispute; what those two reads buy is
 * "Rose Garden entry" instead of a UUID. `Terminals` and `Users` are not read at
 * all: both directories are `OWNER`-only on the API, so a manager would get a
 * refusal for them — and the printed number already names the till that printed
 * it, which is the part somebody is holding.
 */

/** One read, in one of the three states it can be in. */
type Loaded<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly value: T }
  | { readonly kind: 'failed'; readonly error: unknown };

export function Ticket() {
  const client = useApiClient();
  const { id } = useParams<{ id: string }>();

  const [state, setState] = useState<Loaded<TicketResource>>({ kind: 'loading' });

  /** Whether the void confirmation is open. */
  const [voiding, setVoiding] = useState(false);

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    if (id === undefined) return;

    // Two guards, not one. `controller` stops the request itself, and `live`
    // stops an answer that was already in flight from being written into state
    // after this effect was torn down — which is what a second lookup done from
    // this very screen produces.
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(TicketsController_read_v1, { path: { id }, signal: controller.signal })
      .then((ticket) => {
        if (live) setState({ kind: 'loaded', value: ticket });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, id, reloads]);

  const ticket = state.kind === 'loaded' ? state.value : null;

  const item = useNamedItem(ticket?.catalogueItemId);
  const outlet = useNamedOutlet(ticket?.outletId);

  // A route matched on `:id` always has one; this is the shape of somebody
  // reaching the URL some other way. A redirect rather than a not-found screen,
  // because the tickets list is where they were going.
  if (id === undefined) return <Navigate to={paths.tickets} replace />;

  // **`RESOURCE_NOT_FOUND` and `VALIDATION_FAILED` are one answer here, and that
  // is not a conflation.** The first is a ticket that does not exist *or* is
  // outside this caller's reach — indistinguishable on purpose (FR-128) — and
  // the second is an address that is not a UUID at all, which the API refuses
  // before it looks anything up. All three reach this screen the same way: a
  // link that is out of date, or a URL typed by hand. There is nothing here that
  // can tell them apart and nothing below that pretends to.
  const missing =
    state.kind === 'failed' &&
    (isNotFound(state.error) ||
      (state.error instanceof ApiError && state.error.code === 'VALIDATION_FAILED'));

  const voided = ticket !== null && isVoided(ticket);

  return (
    <Screen
      title="Ticket"
      aside={
        <>
          {ticket !== null && (
            <>
              <ScreenFact label="Number">
                <span className="font-mono numeric">{ticket.number}</span>
                {voided && (
                  <>
                    {' '}
                    {/* `outline` and not `destructive`, matching the list. Red in
                        this system means the system refused something or is
                        about to; a void is an ordinary correction that already
                        happened. */}
                    <Badge variant="outline">Voided</Badge>
                  </>
                )}
              </ScreenFact>

              <ScreenFact label="Amount">
                {/* Struck through when voided and still legible: the amount that
                    was taken and given back is exactly the figure somebody
                    reconciling the day needs to read. `formatPaise` is the only
                    thing that turns paise into an amount — nothing here divides
                    by 100. */}
                <span className={`font-mono numeric ${voided ? 'line-through' : ''}`}>
                  {formatPaise(ticket.amountPaise)}
                </span>
              </ScreenFact>
            </>
          )}

          <Link
            to={paths.tickets}
            className="inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            <IconArrowLeft className="size-4" aria-hidden />
            Back to the tickets
          </Link>
        </>
      }
    >
      {/* Above the record, and on this screen as well as the list, because the
          next visitor with a question arrives while somebody is still looking at
          the last one's ticket. */}
      <TicketLookup />

      {missing ? (
        <TicketMissing />
      ) : (
        // **No card.** The panel is the surface, and the record is the only
        // thing on the field under the lookup band — so its own edges were a
        // card inside a card twice over. The lookup's rule above it is the
        // boundary a box was drawing, and the records band supplies the gutter
        // the `p-6` was.
        <div className="pt-2">
          {state.kind === 'loading' && <RecordSkeleton />}

          {state.kind === 'failed' && !missing && (
            <div className="mx-auto max-w-md space-y-3 py-8 text-center">
              <LoadFailed error={state.error} onRetry={reload} />
            </div>
          )}

          {ticket !== null && (
            <Record
              ticket={ticket}
              item={item}
              outlet={outlet}
              voided={voided}
              onVoid={() => {
                setVoiding(true);
              }}
            />
          )}
        </div>
      )}

      <VoidTicketDialog
        ticket={voiding ? ticket : null}
        onClose={() => {
          setVoiding(false);
        }}
        onVoided={(answer) => {
          setVoiding(false);
          // Written straight into state rather than re-read. The API answers
          // both `201` and `200` with the ticket as it now stands — marked — so
          // this is the authoritative copy and a second round trip would only be
          // a chance for it to arrive stale.
          setState({ kind: 'loaded', value: answer });
        }}
      />
    </Screen>
  );
}

/**
 * The record itself.
 *
 * Every mark a voided ticket carries is the one the list row carries — the
 * struck amount, the `outline` badge, the muted treatment — because somebody who
 * saw the row this morning must recognise the same ticket this afternoon.
 *
 * **`itemRetiredAtIssue` is a quiet badge and never a warning.** It means the
 * item was retired between the till's last catalogue fetch and the server
 * accepting the sale (R-306, FR-334). The ticket is valid, nothing was refused
 * and nobody did anything wrong.
 *
 * **The one control it carries is the void**, and it is offered to every signed-
 * in caller who can see the record: `TicketVoidsController` declares
 * `@RequireRole()` with no arguments, so the rule is reach rather than rank — a
 * ticket this screen could read is a ticket this caller may void, until its day
 * closes. It is hidden once the ticket is voided, because there is nothing left
 * to do; whether the ticket's business day still allows one is a question the
 * document *does* let a client ask and this screen chooses not to, for the cost
 * reason `ticket-void.ts` sets out — not, as this comment used to say, because
 * there is no route for it.
 */
function Record({
  ticket,
  item,
  outlet,
  voided,
  onVoid,
}: {
  ticket: TicketResource;
  item: CatalogueItemResource | null;
  outlet: OutletResource | null;
  voided: boolean;
  onVoid: () => void;
}) {
  const gap = describeRecordingGap(ticket.occurredAt, ticket.recordedAt);

  // Three states, not two. `voidedAt` says whether; `voidReason` says why, or
  // nothing at all — and `null` there is never "not voided".
  const reason = readVoidReason(ticket);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div className="space-y-2">
          <p
            className={`font-mono text-2xl numeric ${voided ? 'text-muted-foreground line-through' : 'text-foreground'}`}
          >
            {ticket.number}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            {voided && <Badge variant="outline">Voided</Badge>}

            {/* `secondary` rather than `outline`, so it reads as a note and not
                as the void beside it. */}
            {ticket.itemRetiredAtIssue && <Badge variant="secondary">Retired item</Badge>}
          </div>
        </div>

        <div className="text-right">
          <p className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
            Charged
          </p>
          <p
            className={`mt-1 font-mono text-2xl numeric ${voided ? 'text-muted-foreground line-through' : 'text-foreground'}`}
          >
            {formatPaise(ticket.amountPaise)}
          </p>
        </div>
      </div>

      {reason.kind !== 'not-voided' && (
        // Stated in a sentence as well as in the marks above it, because this is
        // the answer to the question somebody came to the counter with — and
        // then, under it, *why*, which is the question they ask next.
        //
        // Read from `reason` rather than from `voided`, though the two agree:
        // the union is what makes the null branch below unmissable, and a
        // `voided &&` here with the reason tested separately inside is exactly
        // the shape that loses it.
        //
        // It still does not say *when* it was voided. `voidedAt` is a real
        // `string | null` now and a moment could be rendered — the note in
        // `ticket-void.ts` says so — but that is not this ticket's change and
        // it is reported in the PR rather than smuggled in beside one.
        //
        // `text-foreground` on `bg-muted`: the muted-on-muted pairing misses AA
        // and is the one a quiet inset panel invites.
        <div className="space-y-3 rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
          <p>This ticket was voided.</p>

          <div>
            <p className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
              Reason for the void
            </p>

            {reason.kind === 'given' ? (
              // Free text somebody typed, shown as they typed it. Not truncated
              // and not clamped: this is the record, it is what the person at
              // the counter is owed, and the field is bounded at 280 characters
              // anyway.
              <p className="mt-1">{reason.reason}</p>
            ) : (
              // **Never a blank.** A blank here reads as an answer — that the
              // reason was empty, or that the screen is broken, or worst, that
              // the ticket was not voided at all. The second line is why it is
              // not a fault: the till was never asked.
              // Italic on the first line, matching the row: it separates the
              // app speaking about an absence from the free text a reason
              // otherwise is, which is the one thing the two branches must not
              // be confused for.
              <>
                <p className="mt-1 italic">{NO_VOID_REASON_GIVEN}</p>
                <p className="mt-0.5 text-muted-foreground">{NO_VOID_REASON_EXPLAINED}</p>
              </>
            )}
          </div>
        </div>
      )}

      <dl className="grid grid-cols-1 gap-x-8 gap-y-5 border-t border-border pt-6 sm:grid-cols-2">
        <Fact label="Sold">
          {item === null ? (
            // Not a blank and not a guess. The item read may have failed or may
            // still be in flight, and the id is what somebody quotes when they
            // ask about it.
            <span className="font-mono text-xs numeric">{ticket.catalogueItemId}</span>
          ) : (
            <>
              {item.name}{' '}
              <span className="font-mono text-muted-foreground numeric">{item.code}</span>
            </>
          )}
          <span className="mt-0.5 block text-sm text-muted-foreground">
            {labelForItemKind(ticket.itemKind)}
          </span>
        </Fact>

        <Fact label="Outlet">
          {outlet === null ? (
            <span className="font-mono text-xs numeric">{ticket.outletId}</span>
          ) : (
            outlet.name
          )}
        </Fact>

        <Fact label="Printed">{formatInstant(ticket.occurredAt)}</Fact>

        <Fact label="Recorded">
          {formatInstant(ticket.recordedAt)}
          {/* The gap is the account of an outage and is never worded as a
              fault: a ticket that took four hours to arrive is a ticket the
              offline queue did its job for. */}
          {gap !== null && (
            <span className="mt-0.5 block text-sm text-muted-foreground">{gap}</span>
          )}
        </Fact>

        {/* There is deliberately no "counter" fact here, and it is a gap in the
            contract rather than a decision. A till holds two counters and the
            printed form spells both identically — `T03-000871` off the TICKET
            counter and off the RECEIPT counter are two different artefacts,
            which is why the lookup route takes a `sequenceKind` at all — but
            `TicketResource` carries neither `sequenceKind` nor `numberValue`,
            so a record cannot state which counter printed it. Reported in the
            PR; it is not something to infer from the number, because the number
            is exactly what cannot tell you. */}

        <Fact label="Reference">
          <span className="font-mono text-xs numeric">{ticket.id}</span>
        </Fact>
      </dl>

      {isVoidable(ticket) && (
        // Below the record and not beside the number: this is read before it is
        // pressed, and a control at the top of a record is one somebody reaches
        // before they have finished reading which ticket they are looking at.
        //
        // `outline` and not `destructive`, matching how a revocation is offered
        // on the terminals list — the red is spent inside the confirmation,
        // where the irreversible thing actually happens. A screen whose
        // strongest colour sits permanently on a record has nowhere left to go
        // when it needs to warn.
        <div className="flex justify-end border-t border-border pt-6">
          <Button
            variant="outline"
            // The number rather than "Void": what the person confirming is
            // holding is the printed ticket, and it is what they will read back.
            aria-label={`Void ${ticket.number}`}
            onClick={onVoid}
          >
            Void this ticket
          </Button>
        </div>
      )}
    </div>
  );
}

/** One fact under its signage label, the way a standing fact is written. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
        {label}
      </dt>
      <dd className="mt-1 text-foreground">{children}</dd>
    </div>
  );
}

/**
 * The record's shape while it is in flight.
 *
 * A skeleton and not a spinner, matching what lands: `docs/ui-conventions.md`
 * asks for exactly this, because a layout shift under a pointer is how somebody
 * clicks the thing below the one they aimed at.
 *
 * `aria-hidden`, because there is nothing here to read.
 */
function RecordSkeleton() {
  return (
    <div aria-hidden className="space-y-6">
      <div className="flex items-start justify-between gap-8">
        <div className="h-8 w-44 animate-pulse rounded-3xl bg-muted" />
        <div className="h-8 w-28 animate-pulse rounded-3xl bg-muted" />
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-5 border-t border-border pt-6 sm:grid-cols-2">
        {/* Varied widths derived from the position rather than random, so it
            reads as text and does not re-roll on every render. */}
        {[62, 48, 70, 55, 40, 66].map((width) => (
          <div key={width} className="space-y-2">
            <div className="h-3 w-20 animate-pulse rounded-3xl bg-muted" />
            <div
              className="h-4 animate-pulse rounded-3xl bg-muted"
              style={{ width: `${String(width)}%` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * There is no ticket at this address.
 *
 * **Byte for byte the same answer for a ticket that does not exist and one
 * outside the caller's reach**, because the API answers them identically on
 * purpose — a distinguishable refusal is an enumeration oracle (FR-128). This
 * copy says what is honest for both and nothing that implies it could tell.
 *
 * The way out is the lookup above it, which is already on the screen: somebody
 * who followed a stale link still has the number in their hand.
 */
function TicketMissing() {
  return (
    <Alert>
      <IconTicketOff />
      <AlertTitle>There is no ticket at this address</AlertTitle>
      <AlertDescription>Type the ticket&rsquo;s number into the field above.</AlertDescription>
    </Alert>
  );
}

/**
 * What was sold, by id — a name, or nothing at all.
 *
 * `GET /v1/catalogue/items/{id}` is `@RequireRole()` + `@RequireOutletScope()`,
 * so a manager reads it (FR-232) and this asks for exactly the one item the
 * ticket names rather than reading the catalogue whole. A retired item still
 * answers here, which matters: a ticket outlives the item it was sold against.
 *
 * **A failure costs a name and never the screen** — the record renders with the
 * id it already has, which is the honest fallback and the thing somebody quotes.
 */
function useNamedItem(itemId: string | undefined): CatalogueItemResource | null {
  const client = useApiClient();
  const [item, setItem] = useState<CatalogueItemResource | null>(null);

  useEffect(() => {
    if (itemId === undefined) {
      setItem(null);

      return;
    }

    const controller = new AbortController();
    let live = true;

    client
      .request(CatalogueItemsController_read_v1, {
        path: { id: itemId },
        signal: controller.signal,
      })
      .then((answer) => {
        if (live) setItem(answer);
      })
      .catch(() => {
        if (live) setItem(null);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, itemId]);

  return item;
}

/**
 * Which outlet sold it, by id. `GET /v1/outlets/{id}` is `@RequireRole()` +
 * `@RequireOutletScope()`, so it is bounded by the same reach the ticket read
 * was — an outlet whose ticket this caller could read is an outlet they can
 * name. A failure costs a name and never the screen.
 */
function useNamedOutlet(outletId: string | undefined): OutletResource | null {
  const client = useApiClient();
  const [outlet, setOutlet] = useState<OutletResource | null>(null);

  useEffect(() => {
    if (outletId === undefined) {
      setOutlet(null);

      return;
    }

    const controller = new AbortController();
    let live = true;

    client
      .request(OutletsController_read_v1, { path: { id: outletId }, signal: controller.signal })
      .then((answer) => {
        if (live) setOutlet(answer);
      })
      .catch(() => {
        if (live) setOutlet(null);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId]);

  return outlet;
}
